// Native (selectable) PDF content renderer for KANN manuals.
// Draws headings/body/labels as real text via jsPDF; only images stay raster.

const rasterize = (src) =>
  new Promise((res) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = im.naturalWidth || 1000;
        c.height = im.naturalHeight || 1000;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(im, 0, 0);
        res({ dataUrl: c.toDataURL("image/jpeg", 0.92), w: c.width, h: c.height });
      } catch {
        res(null);
      }
    };
    im.onerror = () => res(null);
    im.src = src;
  });

// pdf: jsPDF instance, positioned on the first content page (cursor starts at cTop).
export async function renderManualContent(pdf, D, manual, labels, orient = "portrait") {
  const { pw, cTop, cBot } = D;
  const ML = 18, CW = pw - ML * 2;
  const INK = [30, 30, 30], TEXT = [35, 35, 35], SEC = [107, 107, 107], GOLD = [219, 150, 13], BORDER = [236, 236, 232];

  let cursor = cTop;
  const newPage = () => { pdf.addPage(); cursor = cTop; };
  const ensure = (h) => { if (cursor + h > cBot) newPage(); };

  const para = (text, o = {}) => {
    if (text === undefined || text === null || text === "") return;
    const fs = o.fs || 11, color = o.color || TEXT, x = o.x ?? ML, maxW = o.maxW ?? CW;
    pdf.setFont(o.mono ? "courier" : "helvetica", o.bold ? "bold" : "normal");
    pdf.setFontSize(fs);
    pdf.setTextColor(color[0], color[1], color[2]);
    const lineH = fs * 0.46;
    const lines = pdf.splitTextToSize(String(text), maxW);
    for (const ln of lines) {
      ensure(lineH);
      pdf.text(ln, x, cursor + lineH * 0.72, o.charSpace ? { charSpace: o.charSpace } : undefined);
      cursor += lineH;
    }
    cursor += o.gap || 0;
  };

  const label = (text, color = SEC) => { if (text) para(String(text).toUpperCase(), { fs: 8, color, mono: true, charSpace: 0.6, gap: 2.5 }); };

  const divider = (gap = 6) => { ensure(gap + 1); pdf.setDrawColor(236, 236, 232); pdf.setLineWidth(0.3); pdf.line(ML, cursor, pw - ML, cursor); cursor += gap; };
  const secGap = () => { cursor += 4; };

  const image = async (src, o = {}) => {
    const r = await rasterize(src);
    if (!r || !r.w) return;
    const maxW = o.maxW ?? CW, maxH = o.maxH ?? 90, x = o.x ?? ML;
    let w = maxW, h = (w * r.h) / r.w;
    if (h > maxH) { h = maxH; w = (h * r.w) / r.h; }
    ensure(h);
    pdf.addImage(r.dataUrl, "JPEG", x, cursor, w, h);
    cursor += h + (o.gap || 0);
  };

  const drawNumBox = (x, top, n) => {
    const box = 8;
    pdf.setFillColor(INK[0], INK[1], INK[2]);
    pdf.roundedRect(x, top, box, box, 1.3, 1.3, "F");
    pdf.setTextColor(255, 255, 255); pdf.setFont("helvetica", "bold"); pdf.setFontSize(11);
    pdf.text(String(n).padStart(2, "0"), x + box / 2, top + box / 2, { align: "center", baseline: "middle" });
  };

  const step = async (n, s) => {
    const box = 8, tlh = 12.5 * 0.46, dlh = 11 * 0.46;
    const twoCol = !!s.image; // text left, image right (both orientations)
    const leftW = twoCol ? CW * (orient === "landscape" ? 0.6 : 0.56) : CW;
    const titleX = ML + box + 4, titleW = leftW - box - 4;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(12.5);
    const titleLines = s.title ? pdf.splitTextToSize(s.title, titleW) : [];
    const titleH = titleLines.length * tlh;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11);
    const descLines = s.description ? pdf.splitTextToSize(s.description, titleW) : [];
    const leftH = Math.max(box, titleH) + 1.5 + (descLines.length ? descLines.length * dlh + 2 : 0);

    const drawHead = (top) => {
      drawNumBox(ML, top, n);
      pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]); pdf.setFont("helvetica", "bold"); pdf.setFontSize(12.5);
      let ty = top + tlh * 0.72 + (titleLines.length === 1 ? (box - tlh) / 2 : 0);
      for (const ln of titleLines) { pdf.text(ln, titleX, ty); ty += tlh; }
      let dy = top + Math.max(box, titleH) + 1.5;
      if (descLines.length) {
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(SEC[0], SEC[1], SEC[2]);
        for (const ln of descLines) { pdf.text(ln, titleX, dy + dlh * 0.72); dy += dlh; }
      }
    };

    if (twoCol) {
      const gap = CW * 0.04, rightW = CW - leftW - gap, rightX = ML + leftW + gap;
      const r = await rasterize(s.image);
      let iw = 0, ih = 0;
      if (r && r.w) { iw = rightW; ih = (iw * r.h) / r.w; if (ih > 85) { ih = 85; iw = (ih * r.w) / r.h; } }
      const blockH = Math.max(leftH, ih);
      ensure(blockH + 3);
      const top = cursor;
      drawHead(top);
      if (r && r.w) pdf.addImage(r.dataUrl, "JPEG", rightX + (rightW - iw) / 2, top + Math.max(0, (blockH - ih) / 2), iw, ih);
      cursor = top + blockH + 5;
      return;
    }

    ensure(Math.max(box, titleH) + 2);
    const top = cursor;
    drawHead(top);
    cursor = top + leftH;
    if (s.image) await image(s.image, { x: titleX, maxW: CW - (box + 4), maxH: 75, gap: 3 });
    cursor += 5;
  };

  // Header
  label(labels.guide, GOLD);
  para(manual.product_name || "Productnaam", { fs: 22, bold: true, color: TEXT, gap: 2.5 });
  if (manual.intro) para(manual.intro, { fs: 11.5, color: SEC, gap: 5 });
  if (manual.cover) await image(manual.cover, { maxW: CW, maxH: 95, gap: 5 });
  divider(7);

  // Materials & specifications
  if (manual.materials) { label(labels.materials); para(manual.materials, { fs: 11, color: SEC, gap: 6 }); }
  if (manual.specifications) { label(labels.specifications); para(manual.specifications, { fs: 11, color: SEC, gap: 6 }); }

  // Main steps
  const steps = manual.steps || [];
  if (steps.length) { secGap(); label(labels.steps); cursor += 1.5; for (let i = 0; i < steps.length; i++) await step(i + 1, steps[i]); }

  // Sections
  for (const sec of manual.sections || []) {
    secGap(); divider(7);
    label(sec.title || "—"); cursor += 1.5;
    const ss = sec.steps || [];
    for (let i = 0; i < ss.length; i++) await step(i + 1, ss[i]);
  }

  // Warnings box
  if (manual.warnings) {
    secGap();
    const padX = 6, padY = 5, blh = 11 * 0.46;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11);
    const tw = CW - padX * 2;
    const lines = pdf.splitTextToSize(manual.warnings, tw);
    const boxH = padY * 2 + 5 + lines.length * blh + 2;
    ensure(boxH + 4);
    const top = cursor;
    pdf.setFillColor(252, 246, 233); pdf.roundedRect(ML, top, CW, boxH, 2, 2, "F");
    pdf.setFillColor(GOLD[0], GOLD[1], GOLD[2]); pdf.rect(ML, top, 1.6, boxH, "F");
    pdf.setFont("courier", "bold"); pdf.setFontSize(8); pdf.setTextColor(GOLD[0], GOLD[1], GOLD[2]);
    pdf.text(String(labels.tips || "").toUpperCase(), ML + padX + 2, top + padY + 3, { charSpace: 0.5 });
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    let ty = top + padY + 5 + blh * 0.72;
    for (const ln of lines) { pdf.text(ln, ML + padX + 2, ty); ty += blh; }
    cursor = top + boxH + 5;
  }

  // Disclaimer & warranty
  if (manual.disclaimer) { secGap(); label(labels.disclaimer); para(manual.disclaimer, { fs: 9.5, color: SEC, gap: 5 }); }
  if (manual.warranty) { secGap(); label(labels.warranty); para(manual.warranty, { fs: 11, color: TEXT, gap: 5 }); }

  // Footer (grey), pinned to the bottom of the current page
  const fh = 22;
  if (cursor > cBot - fh) newPage();
  const top = cBot - fh;
  pdf.setFillColor(244, 244, 244); pdf.rect(0, top, pw, fh, "F");
  pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]); pdf.setLineWidth(0.3); pdf.line(0, top, pw, top);
  pdf.setFont("courier", "bold"); pdf.setFontSize(13); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
  pdf.text("KANN", ML, top + 8);
  const kw = pdf.getTextWidth("KANN");
  pdf.setTextColor(GOLD[0], GOLD[1], GOLD[2]); pdf.text(".", ML + kw + 0.4, top + 8);
  if (manual.contact) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(107, 107, 107);
    let cy = top + 13;
    for (const ln of String(manual.contact).split("\n")) { pdf.text(ln, ML, cy); cy += 4; }
  }
  pdf.setFont("courier", "normal"); pdf.setFontSize(8); pdf.setTextColor(107, 107, 107);
  pdf.text(String(labels.support || "").toUpperCase(), pw - ML, top + 8, { align: "right", charSpace: 0.5 });
}
